import {
  JSNS,
  type JsonObject,
  type FilterRule,
  type FilterTests,
  type FilterActions,
  type RawFilterRule,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

export type {
  FilterRule,
  FilterTest,
  FilterTests,
  FilterAction,
  FilterActions,
} from '@zimbra-webif/types';

/** A target for ApplyFilterRules / ApplyOutgoingFilterRules. */
export interface ApplyFilterTarget {
  ruleNames: string[];
  /** restrict to messages matching a query … */
  query?: string;
  /** … or to an explicit set of message ids */
  messageIds?: string[];
}

function asBool(v: unknown): boolean {
  return v === 1 || v === '1' || v === true;
}

/**
 * Map a high-level FilterRule to the raw `filterRule` wire node. The condition
 * (`allof`/`anyof`) lives inside `filterTests`; `active` becomes a 0|1.
 */
function toRawRule(rule: FilterRule): JsonObject {
  // `condition` lives inside filterTests; the rule's own condition wins over
  // any stray copy the caller left in `tests`.
  const filterTests: JsonObject = { ...rule.tests, condition: rule.condition };
  const raw: JsonObject = {
    name: rule.name,
    active: rule.active ? 1 : 0,
    filterTests,
    filterActions: { ...rule.actions },
  };
  if (rule.nestedRule) raw.nestedRule = toRawRule(rule.nestedRule);
  return raw;
}

/** Map a raw `filterRule` node back to a high-level FilterRule. */
function fromRawRule(raw: RawFilterRule): FilterRule {
  const testsNode = Array.isArray(raw.filterTests)
    ? (raw.filterTests[0] ?? {})
    : (raw.filterTests ?? {});
  const actionsNode = Array.isArray(raw.filterActions)
    ? (raw.filterActions[0] ?? {})
    : (raw.filterActions ?? {});
  const { condition, ...tests } = testsNode as FilterTests;
  const rule: FilterRule = {
    name: raw.name ?? '',
    active: asBool(raw.active),
    condition: condition === 'anyof' ? 'anyof' : 'allof',
    tests: tests as FilterTests,
    actions: actionsNode as FilterActions,
  };
  if (raw.nestedRule) rule.nestedRule = fromRawRule(raw.nestedRule);
  return rule;
}

/** Extract the `filterRule[]` array from a Get*FilterRulesResponse. */
function extractRules(raw: {
  filterRules?:
    | { filterRule?: RawFilterRule | RawFilterRule[] }
    | Array<{ filterRule?: RawFilterRule | RawFilterRule[] }>;
}): FilterRule[] {
  const container = Array.isArray(raw.filterRules)
    ? raw.filterRules[0]
    : raw.filterRules;
  const fr = container?.filterRule;
  if (!fr) return [];
  const arr = Array.isArray(fr) ? fr : [fr];
  return arr.map(fromRawRule);
}

/** Wrap high-level rules into the request `{ filterRules: { filterRule: [...] } }`. */
function wrapRules(rules: FilterRule[]): JsonObject {
  return { filterRules: { filterRule: rules.map(toRawRule) } };
}

/**
 * Filters surface (urn:zimbraMail) — Sieve rules for incoming AND outgoing mail.
 *
 * IMPORTANT — full-set replace: ModifyFilterRules / ModifyOutgoingFilterRules
 * REPLACE the entire rule set with the supplied list. There is no per-rule add /
 * edit / delete / reorder command (mail.md open question 6). The UI must GET,
 * mutate the array in memory (including reordering), then PUT the whole set.
 * Concurrency hazard: re-GET before save if two sessions may edit filters.
 */
export class FiltersApi {
  constructor(private readonly client: ZimbraClient) {}

  /* ---------------- incoming ---------------- */

  /** GetFilterRulesRequest — the incoming rule set. */
  async getFilterRules(): Promise<FilterRule[]> {
    const raw = await this.client.call<{
      filterRules?:
        | { filterRule?: RawFilterRule | RawFilterRule[] }
        | Array<{ filterRule?: RawFilterRule | RawFilterRule[] }>;
    }>(JSNS.mail, 'GetFilterRules', {});
    return extractRules(raw);
  }

  /** ModifyFilterRulesRequest — REPLACE the entire incoming rule set. */
  async modifyFilterRules(rules: FilterRule[]): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'ModifyFilterRules', wrapRules(rules));
  }

  /** ApplyFilterRulesRequest — run named incoming rule(s) against messages. */
  async applyFilterRules(req: ApplyFilterTarget): Promise<string[]> {
    return this.apply('ApplyFilterRules', req);
  }

  /* ---------------- outgoing ---------------- */

  /** GetOutgoingFilterRulesRequest — the outgoing rule set. */
  async getOutgoingFilterRules(): Promise<FilterRule[]> {
    const raw = await this.client.call<{
      filterRules?:
        | { filterRule?: RawFilterRule | RawFilterRule[] }
        | Array<{ filterRule?: RawFilterRule | RawFilterRule[] }>;
    }>(JSNS.mail, 'GetOutgoingFilterRules', {});
    return extractRules(raw);
  }

  /** ModifyOutgoingFilterRulesRequest — REPLACE the entire outgoing rule set. */
  async modifyOutgoingFilterRules(rules: FilterRule[]): Promise<JsonObject> {
    return this.client.call(
      JSNS.mail,
      'ModifyOutgoingFilterRules',
      wrapRules(rules),
    );
  }

  /** ApplyOutgoingFilterRulesRequest — run named outgoing rule(s). */
  async applyOutgoingFilterRules(req: ApplyFilterTarget): Promise<string[]> {
    return this.apply('ApplyOutgoingFilterRules', req);
  }

  /* ---------------- shared ---------------- */

  /** Build + send an Apply*FilterRules request; parse the acted-on ids. */
  private async apply(
    command: 'ApplyFilterRules' | 'ApplyOutgoingFilterRules',
    req: ApplyFilterTarget,
  ): Promise<string[]> {
    const body: JsonObject = {
      filterRules: {
        filterRule: req.ruleNames.map((name) => ({ name })),
      },
    };
    if (req.messageIds?.length) body.m = { ids: req.messageIds.join(',') };
    if (req.query) body.query = req.query;
    const raw = await this.client.call<{ m?: { ids?: string } }>(
      JSNS.mail,
      command,
      body,
    );
    const ids = raw.m?.ids;
    return typeof ids === 'string' && ids.length ? ids.split(',') : [];
  }
}
